import { Component, inject, RendererFactory2, Type, ViewChild, ViewContainerRef } from '@angular/core'
import { Subject } from 'rxjs'
import { RedirectorPaynowConfig, RedirectorService } from 'src/app/service/redirector.service'
import { MacroTaskService } from 'src/app/service/task.service'
import { DOCUMENT, NgComponentOutlet } from '@angular/common'
import { StructuredDataTag } from 'src/app/service/meta/structured.data'
import type { PriceSpecification, Product, WithContext } from 'schema-dts'
import { MetaKeyId, MetaService } from 'src/app/service/meta/meta.service'

@Component({
  selector: 'app-buy',
  imports: [
    NgComponentOutlet,
  ],
  templateUrl: './buy.component.html',
  styleUrl: './buy.component.sass',
})
export class BuyComponent {
  protected unsubscribe = new Subject<void>()
  protected update = new Subject<void>()
  protected component!: Type<any>
  protected change = new Subject<void>()
  protected context = this.redirectorService.getContext()
  protected document = inject(DOCUMENT)
  protected metaService = inject(MetaService)
  @ViewChild('template', { static: true, read: ViewContainerRef }) templateRef!: ViewContainerRef

  constructor (
    protected redirectorService: RedirectorService,
    protected macroTaskService: MacroTaskService,
  ) {
    const key = `${this.context.config.type}-${this.context.config.template}`

    const loader = {
      'error-setup': () => import('src/app/buy-page/error/error-setup/error-setup.component').then(m => m.ErrorSetupComponent),
      'error-agp': () => import('src/app/buy-page/error/error-agp/error-agp.component').then(m => m.ErrorAgpComponent),
      'paynow-sicherheit': () => import('src/app/buy-page/paynow/paynow-sicherheit/paynow-sicherheit.component').then(m => m.PaynowSicherheitComponent),
      'paynow-vertrieb': () => import('src/app/buy-page/paynow/paynow-vertrieb/paynow-vertrieb.component').then(m => m.PaynowVertriebComponent),
      'paynow-sold': () => import('src/app/buy-page/paynow/paynow-sold/paynow-sold.component').then(m => m.PaynowSoldComponent),
      'paynow-slim': () => import('src/app/buy-page/paynow/paynow-slim/paynow-slim.component').then(m => m.PaynowSlimComponent),
      '4sale-smart': () => import('src/app/buy-page/forsale/forsale-smart/forsale-smart.component').then(m => m.ForsaleSmartComponent),
      '4sale-expert': () => import('src/app/buy-page/forsale/forsale-expert/forsale-expert.component').then(m => m.ForsaleExpertComponent),
      '4sale-default': () => import('src/app/buy-page/forsale/forsale-default/forsale-default.component').then(m => m.ForsaleDefaultComponent),
      'landing-recently-caught': () => import('src/app/pages/landing/domain-catch/domain-catch.component').then(m => m.DomainCatchComponent),
    }[key] as () => Promise<Type<any>>

    const renderer = inject(RendererFactory2).createRenderer(null, null)

    if (
      this.context.config.type !== 'error'
      && this.context.config.type !== 'landing'
      && this.context.config.template !== 'sold'
    ) {
      renderer.addClass(inject(DOCUMENT).body, `e--color-${this.context.config.template_data['theme-color']}`)
    }

    if (['paynow', '4sale'].includes(this.context.config.type)) {
      const config = this.context.config as RedirectorPaynowConfig
      const price = config.price

      if (price) {
        const data: WithContext<Product> = {
          '@context': 'https://schema.org',
          "@type": "Product",
          image: config?.template_data?.image
            ? config.template_data.image
            : 'https://elitedomains.de/static/images/brand/open-graph.jpg',
          name: $localize`Domain: ` + config.name,
          description: $localize`Die Domain ${config.name}:domainName: steht zum Verkauf. Jetzt kaufen!`,
          offers: {
            '@type': 'Offer',
            price,
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock',
            checkoutPageURLTemplate: `https://checkout.elitedomains.de/${this.context.i18n.locale}/${config.name}`,
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: 0,
                currency: 'EUR'
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 1,
                  unitCode: 'DAY'
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 1,
                  unitCode: 'DAY'
                }
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                name: 'Worldwide'
              },
              // @ts-ignore
              deliveryMethod: "https://schema.org/EmailDelivery"
            }
          },
        }

        if (config.template_data['price-suggest']) {
          // @ts-ignore
          data.offers.priceSpecification = {
            '@type': 'PriceSpecification',
            price,
            priceType: 'Negotiable',
          } as PriceSpecification
        }

        new StructuredDataTag('paynow-offer', data, renderer, this.document)
      }

      if (config?.template_data?.image) {
        this.metaService.addMeta('image' as MetaKeyId, config.template_data.image)
      }
    }

    this.macroTaskService.await(loader())
      .subscribe(next => this.component = next)
  }
}
